import React from 'react';
import Button from '../../../../components/Atoms/Button';
import Badge from '../../../../components/Atoms/Badge';
import { useI18n } from '../../../../i18n';

const ACTIVE = ['queued', 'inprogress'];

export const isBuildActive = (build) =>
  ACTIVE.includes(String(build?.status || '').toLowerCase());

const PHASE_KEY = {
  analysing: 'videoToImages.build.analysing',
  creating: 'videoToImages.build.creating',
  writing: 'videoToImages.build.writing',
};

const STATUS_STYLE = {
  queued: { variant: 'neutral', icon: 'fa-clock' },
  inprogress: { variant: 'info', icon: 'fa-spinner' },
  complete: { variant: 'success', icon: 'fa-check' },
  partiallycomplete: { variant: 'warning', icon: 'fa-exclamation-triangle' },
  failed: { variant: 'danger', icon: 'fa-times' },
  cancelled: { variant: 'neutral', icon: 'fa-ban' },
};

const STATUS_LABEL = {
  queued: 'queued',
  inprogress: 'inProgress',
  complete: 'complete',
  partiallycomplete: 'partiallyComplete',
  failed: 'failed',
  cancelled: 'cancelled',
};

/**
 * What a build is doing, and what it produced.
 *
 * The phase is NAMED rather than left to a bare spinner: the three steps take
 * very different times (analysing is hundreds of forward passes, writing is a
 * MinIO copy per image), so "it is still going" is not the same information as
 * "it is on the slow one".
 */
const DatasetBuildCard = ({ build, handlers }) => {
  const { t } = useI18n();
  if (!build) return null;

  const key = String(build.status || '').toLowerCase();
  const style = STATUS_STYLE[key] || STATUS_STYLE.queued;
  const active = isBuildActive(build);
  const progress = build.progress || {};
  const percent = Number(progress.percent || 0);
  const result = build.result || null;
  const warnings = build.warnings || [];
  const options = build.options || {};

  const phaseLabel = PHASE_KEY[build.phase]
    ? t(PHASE_KEY[build.phase])
    : t(`videoToImages.status.${STATUS_LABEL[key] || 'unknown'}`);

  return (
    <div className={`v2i-build-card ${active ? 'v2i-build-card--active' : ''}`}>
      <div className="v2i-build-card__head">
        <Badge variant={style.variant} icon={style.icon}>
          {t(`videoToImages.status.${STATUS_LABEL[key] || 'unknown'}`)}
        </Badge>
        <span className="v2i-build-card__title">
          {active
            ? t('videoToImages.build.runningHeading')
            : t('videoToImages.build.resultHeading')}
        </span>
        <span className="v2i-build-card__task">
          {options.task === 'bounding_box'
            ? t('videoToImages.build.taskDetection')
            : options.task === 'unlabeled'
              ? t('videoToImages.build.taskUnlabeled')
              : t('videoToImages.build.taskClassification')}
        </span>
        <span className="v2i-build-card__spacer" />
        {active ? (
          <Button variant="outline-danger" size="sm"
            onClick={() => handlers.handleCancelBuild(build._id)}>
            {t('videoToImages.build.cancelBuild')}
          </Button>
        ) : (
          <>
            {result && result.modelDataInfoId && (
              <Button variant="outline-primary" size="sm"
                onClick={() => handlers.handleOpenBuildResult(build)}>
                <i className="fa fa-tags mr-1" aria-hidden="true"></i>
                {t('videoToImages.build.openLabeling')}
              </Button>
            )}
          </>
        )}
      </div>

      {active && (
        <div className="v2i-build-card__body">
          <div className="v2i-progress-cell">
            <div className="v2i-progress">
              <div className="v2i-progress__fill v2i-progress__fill--active"
                style={{ width: `${percent}%` }} />
            </div>
            <span className="v2i-progress-cell__value">{percent}%</span>
          </div>
          <p className="v2i-hint mb-0">
            {phaseLabel}
            {progress.total
              ? ` · ${t('videoToImages.build.progressOf', { done: progress.done || 0, total: progress.total })}`
              : ''}
          </p>
        </div>
      )}

      {!active && result && (
        <div className="v2i-build-card__body">
          <p className="v2i-build-card__result mb-1">
            {t('videoToImages.build.resultLine', {
              imported: result.imported,
              model: result.modelName,
              version: result.version,
              labeled: result.labeled,
            })}
          </p>
          {!!(result.classes || []).length && (
            <p className="v2i-hint mb-0">
              {t('videoToImages.build.resultClasses', {
                names: result.classes.map((entry) => `${entry.name} (${entry.count})`).join(', '),
              })}
            </p>
          )}
        </div>
      )}

      {!!build.error && (
        <div className="v2i-alert v2i-alert--danger mb-0">
          <strong>{t('videoToImages.build.buildFailed')}:</strong> {build.error}
        </div>
      )}

      {!!warnings.length && (
        <div className="v2i-alert v2i-alert--warning mb-0">
          <ul className="v2i-alert__list">
            {warnings.map((warning, index) => <li key={index}>{warning}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
};

export default DatasetBuildCard;
